import React, {memo, useEffect} from "react";
import {Navigate, useParams, useNavigate} from "react-router-dom";
import {CircularLoading} from "../../molecules/loading/CircularLoading";
import {useFetchKuchikomyuSettingCustomerByRestaurantId} from "../../../hooks/kuchikomyu/useFetchKuchikomyuSettingCustomerByRestaurantId";
import {Box} from "@chakra-ui/react";
import {usePostOverallScoreForm} from "../../../hooks/usePostOverallScoreForm";
import {PostOverallScoreForm} from "../../molecules/form/PostOverallScoreForm";
import { useCallback } from "react";
import { customNoAuthAxios } from "../../../utils/customNoAuthAxios";
import { useWatch } from "react-hook-form";
import { useSwrNoAuth } from "../../../hooks/swr/useSwrNoAuth";

type FormValues = { score: number | string };

// デバッグ用：遷移を止めるフラグ（true の間は navigate / location.href を実行しません）
// 本番・通常動作では false にして実際に遷移します。
const DEBUG_PREVENT_REDIRECT = false;
const LOG_TAG = "[Kuchikomyu/PostScore]";

// 追加: オプションでリダイレクトをスキップできるようにする
type Options = {
  skipRedirect?: boolean;
};

// 既存4引数 + 追加の第5引数 options（後方互換）
// 呼び出し側が4引数のままでも問題なく動作します
export const useSubmitKuchikomyuCustomerOverallScore = (
  restaurantId: string,
  branchScore: number,
  gUrl: string,
  defaultUrl: string,
  options?: Options
) => {
  return useCallback(
    async (values: FormValues) => {
      const rawScore = values?.score;
      const scoreNum = Number(rawScore);

      const isHttpUrl = (u?: string) => !!u && /^https?:\/\//i.test(u);
      const isInternalPath = (u?: string) => !!u && u.startsWith("/");

      console.log(LOG_TAG, "submit called", {
        restaurantId,
        rawScore,
        scoreNum,
        scoreIsFinite: Number.isFinite(scoreNum),
        branchScore,
        compareOperator: "> (より大きい場合に g_url)",
        options,
        gUrl,
        defaultUrl,
        gUrl_isHttp: isHttpUrl(gUrl),
        defaultUrl_isHttp: isHttpUrl(defaultUrl),
        defaultUrl_isInternalPath: isInternalPath(defaultUrl),
        DEBUG_PREVENT_REDIRECT,
      });

      try {
        // 1) 総合スコアの登録
        // 送信前ログ
        console.log(LOG_TAG, "POST overall-score start", {
          endpoint: "kuchikomyu/overall-score/customer/",
          payload: { restaurant_id: restaurantId, score: scoreNum }, // ← 修正
        });

        await customNoAuthAxios.post(`kuchikomyu/overall-score/customer/`, {
          restaurant_id: restaurantId,
          score: scoreNum, // ← 修正（overall_score ではなく score）
        });

        console.log(LOG_TAG, "POST overall-score done");

        // 2) unique_question (skipRedirect=true) の場合はここで終了
        if (options?.skipRedirect) {
          console.log(LOG_TAG, "skipRedirect is true → 外部遷移せず終了（アンケート画面へは画面側で遷移）");
          return;
        }

        // 3) 遷移先を計算（低評価は default_url を優先。未設定なら /thanks）
        const isHigh = Number.isFinite(scoreNum) && scoreNum > branchScore;
        const chosenTarget = isHigh ? gUrl : (defaultUrl || '/thanks');

        console.log(LOG_TAG, "redirect decision", {
          isHigh,
          chosenTarget,
          reason: isHigh ? "score > branchScore → g_url" : "score <= branchScore → default_url (fallback /thanks)",
          chosenTarget_isHttp: isHttpUrl(chosenTarget),
          chosenTarget_isInternalPath: isInternalPath(chosenTarget),
        });

        // 4) デバッグ中は遷移を実行しない
        if (DEBUG_PREVENT_REDIRECT) {
          console.log(LOG_TAG, "DEBUG_PREVENT_REDIRECT = true のため、遷移を実行しません。");
          return;
        }

        // 本来の遷移（デバッグ解除後に有効化）
        if (isHttpUrl(chosenTarget)) {
          window.location.href = chosenTarget!;
        } else if (isInternalPath(chosenTarget)) {
          // NOTE: useNavigate はこのスコープでは使っていないため、必要なら引数経由で受け取ってください
          window.location.href = chosenTarget!; // 簡易的にアプリ内パスへ
        } else {
          console.log(LOG_TAG, "chosenTarget が不正。/thanks にフォールバック");
          // gUrl も不正または null の場合は /thanks へ
          window.location.href = "/thanks";
        }
      } catch (error) {
        console.log(LOG_TAG, "POST overall-score error", error);
        // デバッグ中は遷移しない
        if (!DEBUG_PREVENT_REDIRECT) {
           window.location.href = "/thanks";
        }
      }
    },
    [restaurantId, branchScore, gUrl, defaultUrl, options?.skipRedirect]
  );
};

export const PostKuchikomyuOverallScore: React.FC = memo(() => {
  const {restaurantId} = useParams();
  const {kuchikomyuSetting, isLoading} = useFetchKuchikomyuSettingCustomerByRestaurantId(restaurantId);
  // 既回答チェック（404 を未回答扱い、それ以外でデータがあれば回答済み）
  const {
    data: overallScore,
    isLoading: isLoadingOverall,
    error: overallScoreError,
  } = useSwrNoAuth<number | null>(restaurantId ? `kuchikomyu/overall-score/customer/?restaurant_id=${restaurantId}` : null);
  const isNotAnswered = overallScoreError?.response?.status === 404 || overallScore == null;

  useEffect(() => {
    console.log(LOG_TAG, "page init", { restaurantId, isLoading });
  }, [restaurantId, isLoading]);

  useEffect(() => {
    if (!kuchikomyuSetting) return;
    const uqRaw = (kuchikomyuSetting as any).unique_question;
    const uniqueQuestionNormalized =
      uqRaw === 1 || uqRaw === '1' || uqRaw === true;

    console.log(LOG_TAG, "loaded settings", {
      branch_score: kuchikomyuSetting.branch_score,
      g_url: kuchikomyuSetting.g_url,
      default_url: kuchikomyuSetting.default_url,
      unique_question_raw: uqRaw,
      unique_question_normalized: uniqueQuestionNormalized,
    });
  }, [kuchikomyuSetting]);


  if (isLoading || isLoadingOverall) return <CircularLoading/>;
  if (!kuchikomyuSetting || !restaurantId) return null;

  // 回答済みなら（＝多重投稿の防止）サンクス等へ遷移
  if (!isNotAnswered) {
    return <Navigate replace to={'/thanks'} />;
  }

  return (
    <Box p={4}>
      <ScorePost
        restaurantId={restaurantId}
        branchScore={kuchikomyuSetting.branch_score}
        gUrl={kuchikomyuSetting.g_url}
        defaultUrl={kuchikomyuSetting.default_url}
        uniqueQuestion={
          ((kuchikomyuSetting as any).unique_question === 1) ||
          ((kuchikomyuSetting as any).unique_question === '1') ||
          ((kuchikomyuSetting as any).unique_question === true)
        }
      />
    </Box>

  );
});

const ScorePost: React.FC<{
  restaurantId: string,
  branchScore: number,
  gUrl: string,
  defaultUrl: string,
  uniqueQuestion: boolean
}> = memo(({restaurantId, branchScore, gUrl, defaultUrl, uniqueQuestion}) => {　
    const navigate = useNavigate();
    const {handleSubmit, control, isSubmitting, errors} = usePostOverallScoreForm();

  // 星選択の値を監視して逐次ログを出す
  const watchedScore = useWatch({ control, name: "score" });

  useEffect(() => {
    const n = Number(watchedScore);
    const fin = Number.isFinite(n);
    const preview = fin
      ? (n > branchScore ? "g_url" : (uniqueQuestion ? "unique_survey" : "default_url"))
      : undefined;
    console.log(LOG_TAG, "star changed", {
      watchedScore,
      asNumber: n,
      isFinite: fin,
      branchScore,
      compareOperator: "> (より大きい場合に g_url)",
      willGo: preview,
      gUrl,
      defaultUrl,
      uniqueQuestion,
    });
  }, [watchedScore, branchScore, gUrl, defaultUrl, uniqueQuestion]);

  useEffect(() => {
    console.log(LOG_TAG, "render ScorePost", {
      restaurantId,
      uniqueQuestion,
      gUrl,
      defaultUrl,
      isSubmitting,
      hasErrors: !!errors && Object.keys(errors || {}).length > 0,
      DEBUG_PREVENT_REDIRECT,
    });
  }, [restaurantId, uniqueQuestion, gUrl, defaultUrl, isSubmitting, errors]);

  const submitOverallOnly = useSubmitKuchikomyuCustomerOverallScore(
    restaurantId, branchScore, gUrl, defaultUrl, { skipRedirect: true }
  );

  const onSubmit = async (values: any) => {
    const scoreNum = Number(values?.score);

    // 1) 総合スコアの登録のみ（外部リダイレクトはスキップ）
    await submitOverallOnly(values);

    // 2) 遷移先を事前決定
    const isHigh = Number.isFinite(scoreNum) && scoreNum > branchScore;
    const nextUrl = isHigh ? gUrl : (defaultUrl || '/thanks');

    try {
      sessionStorage.setItem("kuchikomyu:nextUrl", nextUrl);
    } catch (_) { /* noop */ }

    // 3) 変更点: unique_question=0（false）かつ低評価のみアンケートへ
    if (!uniqueQuestion && !isHigh) {
      navigate(`/kuchikomyu/survey/${restaurantId}`, {
        replace: true,
        state: { nextUrl },
      });
      return;
    }

    // 4) それ以外は即座に nextUrl へ
    const isHttpUrl = (u?: string) => !!u && /^https?:\/\//i.test(u!);
    const isInternalPath = (u?: string) => !!u && u!.startsWith("/");

    if (isHttpUrl(nextUrl)) {
      if (!DEBUG_PREVENT_REDIRECT) window.location.href = nextUrl!;
      return;
    }
    if (isInternalPath(nextUrl)) {
      if (!DEBUG_PREVENT_REDIRECT) navigate(nextUrl!, { replace: true });
      return;
    }

    if (!DEBUG_PREVENT_REDIRECT) {
      if (isHttpUrl(gUrl)) window.location.href = gUrl;
      else navigate(`/thanks`);
    }
  };


  return (
    <PostOverallScoreForm
      onSubmit={handleSubmit(onSubmit)}
      control={control}
      isSubmitting={isSubmitting}
      errors={errors}
    />
  );
});